import { FC, ReactNode, useEffect, useState } from 'react';
import { Col, Dropdown, Menu, Row } from 'antd';
import { TbCircleCheckFilled } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { DropdownMenuPanel } from '@/shared/components/dropdowns/DropdownPanel';
import { WORKSPACE_LIST_TYPE_ICON_REGISTRY } from '@/shared/components/workspace-resources';
import { useListStore } from '@/stores';
import { ListType, SetStateMethod } from '@/types';

interface ListTypeSelectionDropdownProps {
    listType: ListType;
    setListType: SetStateMethod<ListType>;
    children?: ReactNode;
}

const ListTypeSelectionDropdown: FC<ListTypeSelectionDropdownProps> = ({
    listType,
    setListType,
    children,
}): JSX.Element => {
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(false);
    }, []);

    const { token } = useAntDesignTheme();
    const PeopleListIcon = WORKSPACE_LIST_TYPE_ICON_REGISTRY.people;
    const CompaniesListIcon = WORKSPACE_LIST_TYPE_ICON_REGISTRY.companies;
    const CustomListIcon = WORKSPACE_LIST_TYPE_ICON_REGISTRY.custom;

    const items = [
        {
            key: ListType.PEOPLE,
            icon: <PeopleListIcon size='1rem' style={{ verticalAlign: '-0.175rem', marginRight: '0.25rem' }} />,
            description: 'Lists composed of the people synced from your email & calendars.',
        },
        {
            key: ListType.COMPANIES,
            icon: <CompaniesListIcon size='1rem' style={{ verticalAlign: '-0.175rem', marginRight: '0.25rem' }} />,
            description: 'Lists composed of the companies synced from your email & calendars.',
        },
        {
            key: ListType.CUSTOM,
            icon: <CustomListIcon size='1rem' style={{ verticalAlign: '-0.175rem', marginRight: '0.25rem' }} />,
            description:
                'Lists composed of both people and company records. Recommended for cross-functional due diligence workflows.',
        },
    ];

    const { selectedList, updateList } = useListStore();

    const type = listType ?? selectedList?.type;

    return (
        <Dropdown
            trigger={['click']}
            placement='bottomRight'
            popupRender={() => (
                <DropdownMenuPanel
                    style={{ maxWidth: 16 * 20, marginTop: '0.75rem' }}
                    onClick={(e) => {
                        if (selectedList?.id) {
                            updateList(selectedList?.id as string, { type: e.key as ListType });
                        } else if (listType) {
                            setListType(e.key as ListType);
                        }
                    }}
                >
                    <div style={{ padding: '0 0.75rem', marginBottom: '0.325rem' }}>
                        <small style={{ color: token.colorTextTertiary }}>List Types</small>
                    </div>
                    {items?.map((i) => {
                        return (
                            <Menu.Item key={i?.key}>
                                <Row wrap={false} align='top'>
                                    <Col flex='none'>
                                        {i?.icon}
                                        {i?.key}
                                    </Col>
                                    {type === i?.key && (
                                        <Col flex='none'>
                                            <TbCircleCheckFilled
                                                style={{
                                                    color: token.colorPrimary,
                                                    verticalAlign: '-0.1rem',
                                                    marginLeft: '0.5rem',
                                                }}
                                            />
                                        </Col>
                                    )}
                                    <Col flex='auto' />
                                </Row>
                                <small style={{ color: token.colorTextTertiary }}>{i?.description}</small>
                            </Menu.Item>
                        );
                    })}
                </DropdownMenuPanel>
            )}
        >
            <span>{children}</span>
        </Dropdown>
    );
};

export default ListTypeSelectionDropdown;
